import { ScaleType } from '@/types'

type SSATScaleMap = Record<'Verbal' | 'Quantitative' | 'Reading', Record<number, number>>

const generateScaleScores = (baseScores: Record<number, number>, min: number, max: number): Record<number, number> => {
  const scores: Record<number, number> = {}
  const baseEntries = Object.entries(baseScores).map(([k, v]) => [parseInt(k), v])
  baseEntries.sort((a, b) => a[0] - b[0])

  // Fill all scores from min to max
  for (let raw = min; raw <= max; raw++) {
    // Set 500 for any score below 0
    if (raw < 0) {
      scores[raw] = 500
      continue
    }

    if (raw in baseScores) {
      scores[raw] = baseScores[raw]
      continue
    }

    // Find surrounding base scores for interpolation
    const lower = baseEntries.filter(([k]) => k <= raw).slice(-1)[0]
    const upper = baseEntries.find(([k]) => k >= raw)

    if (!lower || !upper) {
      scores[raw] = 500
      continue
    }

    // Linear interpolation
    const [rawLower, scaleLower] = lower
    const [rawUpper, scaleUpper] = upper
    if (rawLower === rawUpper) {
      scores[raw] = scaleLower
    } else {
      const ratio = (raw - rawLower) / (rawUpper - rawLower)
      const interpolated = Math.round(scaleLower + ratio * (scaleUpper - scaleLower))
      scores[raw] = interpolated
    }
  }

  return scores
}

const SSAT_UPPER_VERBAL_BASE = {
  60: 800,
  55: 800,
  50: 780,
  45: 750,
  40: 725,
  35: 700,
  30: 645,
  25: 645,
  20: 615,
  15: 590,
  10: 565,
  5: 530,
  0: 505,
  [-5]: 500,
}

const SSAT_UPPER_MATH_BASE = {
  50: 800,
  45: 780,
  40: 755,
  35: 725,
  30: 700,
  25: 670,
  20: 640,
  15: 615,
  10: 585,
  5: 555,
  0: 530,
  [-5]: 500,
}

const SSAT_UPPER_READING_BASE = {
  40: 800,
  35: 720,
  30: 690,
  25: 660,
  20: 630,
  15: 600,
  10: 570,
  5: 540,
  0: 510,
  [-5]: 500,
}

const SSAT_MIDDLE_VERBAL_BASE = {
  60: 710,
  55: 710,
  50: 707,
  45: 695,
  40: 677,
  35: 656,
  30: 635,
  25: 617,
  20: 596,
  15: 551,
  10: 528,
  5: 506,
  0: 479,
  [-5]: 440,
}

const SSAT_MIDDLE_MATH_BASE = {
  50: 710,
  45: 692,
  40: 674,
  35: 656,
  30: 638,
  25: 619,
  20: 599,
  15: 578,
  10: 530,
  5: 506,
  0: 479,
  [-5]: 440,
}

const SSAT_MIDDLE_READING_BASE = {
  40: 710,
  35: 701,
  30: 671,
  25: 641,
  20: 614,
  15: 587,
  10: 557,
  5: 524,
  0: 497,
  [-5]: 440,
}

export const SSAT_UPPER_SCALE: SSATScaleMap = {
  Reading: generateScaleScores(SSAT_UPPER_READING_BASE, -10, 40),
  Verbal: generateScaleScores(SSAT_UPPER_VERBAL_BASE, -15, 60),
  Quantitative: generateScaleScores(SSAT_UPPER_MATH_BASE, -13, 50),
}

export const SSAT_MIDDLE_SCALE: SSATScaleMap = {
  Verbal: generateScaleScores(SSAT_MIDDLE_VERBAL_BASE, -15, 60),
  Quantitative: generateScaleScores(SSAT_MIDDLE_MATH_BASE, -13, 50),
  Reading: generateScaleScores(SSAT_MIDDLE_READING_BASE, -10, 40),
}

export const SSAT_UPPER_SCORE_RANGE: ScaleType = {
  MIN: 500,
  MAX: 800,
  LOWER_BOUND: 1500,
  UPPER_BOUND: 2400,
}

export const SSAT_MIDDLE_SCORE_RANGE: ScaleType = {
  MIN: 440,
  MAX: 710,
  LOWER_BOUND: 1320,
  UPPER_BOUND: 2130,
}
